import type {
	ConsoleUser,
	PlatformUser,
	PlatformUserApiResponse,
	UserInfo,
	UserListResponse,
	UserSettings,
} from '@/stores/auth'
import { isConsoleDeployment } from '@/utils/deployment'
import type { AxiosRequestConfig } from 'axios'
import { apiCall, consoleApiCall, tenantScopedConfig } from './axiosInstance'

export function mapResponseToPlatformUser(response: PlatformUserApiResponse): PlatformUser {
	const tenantIds = response.tenants.map(t => t.id)
	const role = response.user.role
		? {
				...response.user.role,
				permissions: response.user.role.permissions,
			}
		: null
	return {
		name: response.user.name,
		email: response.user.email,
		tenants: tenantIds,
		tenant_list: response.tenants,
		tenant_id: response.user.role?.tenant_id || tenantIds[0],
		role,
		settings: response.user.settings,
		organizations: response.organizations,
		'tenant-settings': response.tenant_settings,
	}
}

export const usersApi = {
	getCurrentUser: async (tenantId?: string): Promise<ConsoleUser | PlatformUser> => {
		if (isConsoleDeployment()) {
			return await consoleApiCall<ConsoleUser>('/v1/me')
		}

		const options: AxiosRequestConfig | undefined = tenantId
			? { headers: { 'x-bytebeam-tenant': tenantId } }
			: undefined
		const response = await apiCall<PlatformUserApiResponse>('/v3/me', options)
		return mapResponseToPlatformUser(response)
	},
	getUsers: async (organizationId?: string): Promise<UserListResponse> => {
		if (!organizationId) {
			return apiCall('/v3/users')
		}

		const res = await apiCall<{
			users: Array<{ tenant_id: string; name: string; email: string; role_name: string }>
		}>(`/v3/organizations/${encodeURIComponent(organizationId)}/users`)

		return {
			users: (res.users ?? []).map(user => ({
				name: user.name,
				email: user.email,
				role_name: user.role_name,
				tenantId: user.tenant_id,
			})),
		}
	},
	createUser: async (userData: Partial<UserInfo>, tenantId?: string): Promise<{ status: string }> => {
		const options: AxiosRequestConfig = {
			method: 'POST',
			data: userData,
			...tenantScopedConfig(tenantId),
		}
		return apiCall('/v3/users', options)
	},
	updateUser: async (userEmail: string, roleName: string, tenantId?: string): Promise<{ status: string }> =>
		apiCall('/v3/users', {
			method: 'PATCH',
			data: {
				user_email: userEmail,
				role_name: roleName,
			},
			...(tenantId && {
				headers: {
					'x-bytebeam-tenant': tenantId,
				},
			}),
		}),
	deleteUser: async (userEmail: string, tenantId?: string): Promise<{ status: string }> =>
		apiCall('/v3/users', {
			method: 'DELETE',
			data: {
				email: userEmail,
			},
			...tenantScopedConfig(tenantId),
		}),
}

// Backend replaces the settings JSON. Use authStore.updateUserSettings to merge current settings.
export const updateCurrentUserSettings = async (settings: UserSettings): Promise<{ status: string }> =>
	apiCall('/v3/me', {
		method: 'PATCH',
		data: { settings },
	})

export const updateCurrentUserName = async (name: string): Promise<{ status: string }> =>
	apiCall('/v3/me', {
		method: 'PATCH',
		data: { name },
	})
